/** Public, read-only view of a shared canvas (/share/:token). No login required. */
import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { Download, Sparkles } from "lucide-react";
import { getSharedCanvas, type SharedCanvas } from "./api";
import CanvasView from "./components/canvas/CanvasView";
import { downloadPdf, downloadPng } from "./components/canvas/exportCanvas";
import "./studio.css";

export default function SharedCanvasPage() {
  const { token = "" } = useParams();
  const ref = useRef<HTMLDivElement>(null);
  const [shared, setShared] = useState<SharedCanvas | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    getSharedCanvas(token)
      .then(setShared)
      .catch((err) => setError(err instanceof Error ? err.message : "This canvas could not be loaded."));
  }, [token]);

  const buttonClass =
    "flex items-center gap-1.5 rounded-lg border border-border-default-t px-3 py-1.5 text-[13px] font-semibold text-secondary-t hover:bg-hover-bg";

  return (
    <div className="min-h-screen bg-base" style={{ fontFamily: "var(--font-sans)", color: "var(--text-primary)" }}>
      <header className="flex items-center gap-3 border-b border-border-subtle-t bg-surface px-6 py-3">
        <Sparkles className="h-5 w-5 text-adrsta-blue" />
        <span className="font-bold text-primary-t">Adrsta</span>
        <span className="text-[13px] text-tertiary-t">Shared canvas</span>
        {shared && (
          <div className="ml-auto flex gap-2">
            <button onClick={() => ref.current && downloadPng(ref.current, shared.title)} className={buttonClass}>
              <Download className="h-4 w-4" /> PNG
            </button>
            <button onClick={() => ref.current && downloadPdf(ref.current, shared.title)} className={buttonClass}>
              <Download className="h-4 w-4" /> PDF
            </button>
          </div>
        )}
      </header>
      <main className="mx-auto max-w-5xl p-6">
        {error && (
          <p className="rounded-lg px-4 py-3 text-[13px]" style={{ background: "var(--error-subtle)", color: "var(--error)" }}>
            {error}
          </p>
        )}
        {!shared && !error && <p className="text-[13px] text-tertiary-t">Loading…</p>}
        {shared && (
          <>
            <p className="mb-3 text-xs text-tertiary-t">
              {shared.agentName ? `Created with ${shared.agentName} · ` : ""}
              {new Date(shared.createdAt).toLocaleString()}
            </p>
            <div className="overflow-hidden rounded-card border border-border-default-t">
              <CanvasView ref={ref} canvas={shared.canvas} />
            </div>
          </>
        )}
      </main>
    </div>
  );
}
